import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';

/**
 * AddressBookModal component for selecting customer addresses
 * Similar to PaperSelector but for address book entries
 */
const AddressBookModal = ({ 
  isOpen, 
  onClose, 
  onSelectAddress,
  onAddNewAddress,
  className
}) => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState('');

  // Load addresses when modal opens
  useEffect(() => {
    if (isOpen) {
      loadAddresses();
    }
  }, [isOpen]);

  const API_URL = process.env.REACT_APP_API_URL || '';

  const loadAddresses = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/address-book`, {
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error('Failed to load address book');
      }
      
      const data = await response.json();
      setAddresses(data);
    } catch (err) {
      setError('Failed to load address book');
      console.error('Error loading addresses:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter addresses based on search query
  const filteredAddresses = addresses.filter(address => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      address.customer_name.toLowerCase().includes(query) ||
      (address.company_name && address.company_name.toLowerCase().includes(query)) ||
      address.address_line1.toLowerCase().includes(query) ||
      address.city.toLowerCase().includes(query) ||
      address.postcode.toLowerCase().includes(query)
    );
  });

  const handleSelectAddress = (address) => {
    onSelectAddress(address);
    onClose();
  };

  const handleAddNew = () => {
    onAddNewAddress();
    onClose();
  };

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog 
        as="div" 
        className="relative z-[60]" 
        onClose={onClose}
      >
        {/* Background overlay */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 mb-4">
                  Select Customer Address
                </Dialog.Title>
                
                {/* Search and controls */}
                <div className="mb-4 space-y-2">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search customers, companies, or addresses..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full p-2 pl-8 border rounded"
                    />
                    <svg className="w-4 h-4 absolute left-2 top-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">
                      {filteredAddresses.length} address{filteredAddresses.length !== 1 ? 'es' : ''} found
                    </span>
                    <button
                      type="button"
                      onClick={handleAddNew}
                      className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Add New Customer
                    </button>
                  </div>
                </div>
                
                {/* Error message */}
                {error && (
                  <div className="mb-4 p-2 bg-red-100 text-red-700 rounded text-sm">
                    {error}
                  </div>
                )}
                
                {/* Address list */}
                <div className="max-h-72 overflow-y-auto">
                  {loading ? (
                    <div className="text-center py-8 text-gray-500">
                      Loading addresses...
                    </div>
                  ) : filteredAddresses.length > 0 ? (
                    <div className="space-y-2">
                      {filteredAddresses.map(address => (
                        <button
                          key={address.id}
                          type="button"
                          className="w-full text-left p-3 border rounded hover:bg-gray-50 focus:bg-blue-50 focus:border-blue-300"
                          onClick={() => handleSelectAddress(address)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">
                                {address.customer_name}
                                {Boolean(address.is_default) && (
                                  <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                    Default
                                  </span>
                                )}
                              </div>
                              {address.company_name && (
                                <div className="text-sm text-gray-600">
                                  {address.company_name}
                                </div>
                              )}
                              <div className="text-sm text-gray-700 mt-1">
                                {address.address_line1}
                                {address.address_line2 && <br />}
                                {address.address_line2}
                                <br />
                                {address.city}, {address.postcode}
                              </div>
                              {address.delivery_instructions && (
                                <div className="text-xs text-gray-500 mt-1">
                                  📝 {address.delivery_instructions}
                                </div>
                              )}
                            </div>
                            <div className="text-xs text-gray-400 ml-2">
                              {new Date(address.updated_at).toLocaleDateString()}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      {searchQuery ? 'No addresses match your search.' : 'No addresses found. Add your first customer address to get started.'}
                    </div>
                  )}
                </div>
                
                {/* Modal actions */}
                <div className="mt-4 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default AddressBookModal;
